import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import { Fragment, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Paper, Typography, Box, Stack, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup, FormControl, InputLabel, Select, MenuItem, Chip, Avatar } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import FingerprintIcon from "@mui/icons-material/Fingerprint";

import SizeSoftXLayout from "../leyout/SizeSoftXLayout";
import MnuControlAsistenciaActions from "./MnuControlAsistenciaActions";
import ModalEnrolarHuella from "./ModalEnrolarHuella";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import {
  apiAccesosList, apiTiemposHoy, apiTiemposRegistrar, apiTiemposUltimaLectura,
  serverSeguridad, serverAdministrativo
} from "../../settings/ApiConfig";

const PRINTERTOOLS_URL = "http://localhost:4000";
const PERFIL_HUELLA = "tenant";
const TIPOS_TIEMPO = [
  "Hora Entrada",
  "Hora salida a comer",
  "Hora entrada de comida",
  "Hora salida",
];

const snapshotRegistros = (registros) => JSON.stringify(
  (registros || []).map((item) => ({
    id: item.id,
    tipo: item.tiporegistro?.trim(),
    hora: item.hora?.trim(),
  }))
);

export default function FrmControlAsistencia() {
  const navigate = useNavigate();
  const { handleOpenAlert } = useLayout();
  const { GetRequest, PostRequest } = useRequest();
  const handleClickRegresarMainDashboard = useCallback(() => navigate("/"), [navigate]);

  const getRequestRef = useRef(GetRequest);
  const postRequestRef = useRef(PostRequest);
  const openAlertRef = useRef(handleOpenAlert);
  getRequestRef.current = GetRequest;
  postRequestRef.current = PostRequest;
  openAlertRef.current = handleOpenAlert;

  const [usuarios, setUsuarios] = useState([]);
  const [usuarioSel, setUsuarioSel] = useState(null);
  const [clave, setClave] = useState("");
  const [registrosHoy, setRegistrosHoy] = useState([]);
  const [registrando, setRegistrando] = useState(false);
  const [modoRegistro, setModoRegistro] = useState("auto");
  const [tipoManual, setTipoManual] = useState(TIPOS_TIEMPO[0]);
  const [lectorArmado, setLectorArmado] = useState(false);
  const [lectorEstado, setLectorEstado] = useState("inactivo");
  const [lectorMensaje, setLectorMensaje] = useState("");
  const [camaraActiva, setCamaraActiva] = useState(false);
  const [fotoBase64, setFotoBase64] = useState(null);
  const [huellaSel, setHuellaSel] = useState(null);
  const [modalEnrolamiento, setModalEnrolamiento] = useState(false);

  const claveRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const usuarioCargadoRef = useRef(null);
  const snapshotRef = useRef(null);
  const consultaEnCursoRef = useRef(false);
  const ultimaLecturaIdRef = useRef(0);

  const tiposRegistrados = registrosHoy.map((registro) => registro.tiporegistro?.trim());

  const detenerCamara = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCamaraActiva(false);
    setFotoBase64(null);
  }, []);

  const cancelarLector = useCallback(async (evento = "cancelar", notificarPrinterTools = true) => {
    setLectorArmado(false);
    setLectorEstado("inactivo");
    if (!notificarPrinterTools) return;
    try {
      await fetch(`${PRINTERTOOLS_URL}/api/huella/evento`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ evento }),
        keepalive: true,
      });
    } catch {
      // PrinterTools puede no estar instalado en esta estación.
    }
  }, []);

  const cargarUsuarios = useCallback(async () => {
    const respuesta = await getRequestRef.current({
      url: apiAccesosList,
      loader: "Cargando accesos del sistema",
    });
    if (!respuesta.hasError && respuesta.result) {
      const accesos = respuesta.result
        .filter((item) => item.usuario?.trim())
        .map((item) => ({
          id: Number(item.id),
          usuario: item.usuario.trim(),
          nombre: item.nombreusuario?.trim() || item.usuario.trim(),
        }));
      setUsuarios(accesos);
      return;
    }
    setUsuarios([]);
    openAlertRef.current(respuesta.message || "No fue posible cargar los accesos del sistema", "error");
  }, []);

  const cargarRegistrosHoy = useCallback(async (usuario, forzar = false) => {
    if (!usuario || consultaEnCursoRef.current) return false;
    consultaEnCursoRef.current = true;
    try {
      const respuesta = await getRequestRef.current({
        url: apiTiemposHoy.replace("{usuario}", encodeURIComponent(usuario)),
      });
      const registros = !respuesta.hasError && respuesta.result ? respuesta.result : [];
      const snapshot = snapshotRegistros(registros);
      if (!forzar && usuarioCargadoRef.current === usuario && snapshotRef.current === snapshot) {
        return false;
      }
      usuarioCargadoRef.current = usuario;
      snapshotRef.current = snapshot;
      setRegistrosHoy(registros);
      return true;
    } finally {
      consultaEnCursoRef.current = false;
    }
  }, []);

  const establecerLineaBase = useCallback(async () => {
    try {
      const respuesta = await getRequestRef.current({ url: apiTiemposUltimaLectura });
      ultimaLecturaIdRef.current = !respuesta.hasError && respuesta.result
        ? Number(respuesta.result.id || 0)
        : 0;
    } catch {
      ultimaLecturaIdRef.current = 0;
    }
  }, []);

  const armarLector = useCallback(async (usuario, tipoRegistro, evento) => {
    if (!usuario || document.visibilityState !== "visible") return;
    await cancelarLector("reconfigurar_lectura");
    setLectorEstado("preparando");
    setLectorMensaje("");
    await establecerLineaBase();
    if (document.visibilityState !== "visible") return;
    try {
      const respuesta = await fetch(`${PRINTERTOOLS_URL}/api/huella/evento`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          evento,
          perfil: PERFIL_HUELLA,
          usuario,
          tipoRegistro,
          seguridadUrl: serverSeguridad,
          administrativoUrl: serverAdministrativo,
          tenantAccessToken: localStorage.getItem("sizesoftx-tenant-access-token") || "",
        }),
      });
      const contenido = await respuesta.json();
      if (!contenido.success) {
        setLectorEstado("error");
        const mensaje = contenido.message || "No fue posible preparar el lector";
        setLectorMensaje(mensaje);
        openAlertRef.current(mensaje, "warning");
        return;
      }
      setLectorEstado("armado");
      setLectorMensaje(contenido.message || "Lector preparado");
      setLectorArmado(true);
    } catch {
      setLectorEstado("no_disponible");
      setLectorMensaje("PrinterTools no esté disponible");
      openAlertRef.current("PrinterTools no esté disponible", "warning");
    }
  }, [cancelarLector, establecerLineaBase]);

  useEffect(() => { cargarUsuarios(); }, [cargarUsuarios]);

  useEffect(() => {
    if (!lectorArmado || !usuarioSel?.usuario) return undefined;
    let activo = true;
    let consultando = false;

    const consultar = async () => {
      if (!activo || consultando || document.visibilityState !== "visible") return;
      consultando = true;
      try {
        const respuesta = await getRequestRef.current({ url: apiTiemposUltimaLectura });
        const lectura = !respuesta.hasError ? respuesta.result : null;
        const id = Number(lectura?.id || 0);
        if (!lectura || id <= ultimaLecturaIdRef.current) return;
        ultimaLecturaIdRef.current = id;
        if (lectura.nombrevendedor?.trim() !== usuarioSel.usuario) return;

        setLectorArmado(false);
        setLectorEstado("confirmado");
        setLectorMensaje(lectura.tiporegistro?.trim() || "Registro confirmado");
        await cargarRegistrosHoy(usuarioSel.usuario, true);
        openAlertRef.current(`${lectura.tiporegistro?.trim()} registrada exitosamente`, "success");
      } finally {
        consultando = false;
      }
    };

    const intervalo = setInterval(consultar, 1000);
    consultar();
    return () => {
      activo = false;
      clearInterval(intervalo);
    };
  }, [lectorArmado, usuarioSel, cargarRegistrosHoy]);

  useEffect(() => {
    const manejarVisibilidad = () => {
      if (document.visibilityState !== "visible") {
        cancelarLector("ventana_oculta");
      }
    };
    document.addEventListener("visibilitychange", manejarVisibilidad);
    return () => {
      document.removeEventListener("visibilitychange", manejarVisibilidad);
      cancelarLector("ventana_cerrada");
      detenerCamara();
    };
  }, [cancelarLector, detenerCamara]);

  const cargarHuellaUsuario = useCallback(async (usuario) => {
    setHuellaSel(null);
    if (!usuario) return;
    try {
      const respuesta = await fetch(
        `${PRINTERTOOLS_URL}/api/huella/imagen?perfil=${PERFIL_HUELLA}&usuario=${encodeURIComponent(usuario)}`
      );
      const contenido = await respuesta.json();
      if (!contenido.success || !contenido.imagen) return;
      setHuellaSel({
        usuario: contenido.usuario || usuario,
        nombre: contenido.nombre || "",
        imagen: contenido.imagen,
        sincronizada: Boolean(contenido.sincronizada),
      });
    } catch {
      // PrinterTools puede no estar instalado en esta estación.
    }
  }, []);

  const seleccionarUsuario = async (nuevoUsuario) => {
    if (!nuevoUsuario) {
      await cancelarLector("usuario_limpiado");
    }
    setUsuarioSel(nuevoUsuario);
    setClave("");
    setFotoBase64(null);
    setHuellaSel(null);
    snapshotRef.current = null;
    if (!nuevoUsuario) {
      usuarioCargadoRef.current = null;
      setRegistrosHoy([]);
      return;
    }
    usuarioCargadoRef.current = nuevoUsuario.usuario;
    await cargarRegistrosHoy(nuevoUsuario.usuario, true);
    const tipo = modoRegistro === "auto" ? "auto" : tipoManual;
    await armarLector(nuevoUsuario.usuario, tipo, "usuario_seleccionado");
    await cargarHuellaUsuario(nuevoUsuario.usuario);
  };

  const cambiarModo = async (_, nuevoModo) => {
    if (!nuevoModo) return;
    setModoRegistro(nuevoModo);
    if (usuarioSel?.usuario) {
      await armarLector(
        usuarioSel.usuario,
        nuevoModo === "auto" ? "auto" : tipoManual,
        "tipo_registro_cambiado"
      );
    }
  };

  const cambiarTipoManual = async (evento) => {
    const tipo = evento.target.value;
    setTipoManual(tipo);
    if (usuarioSel?.usuario) {
      await armarLector(usuarioSel.usuario, tipo, "tipo_registro_cambiado");
    }
  };

  const iniciarCamara = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 320, height: 240, facingMode: "user" },
      });
      if (videoRef.current) videoRef.current.srcObject = stream;
      streamRef.current = stream;
      setCamaraActiva(true);
    } catch {
      setCamaraActiva(false);
    }
  };

  const capturarFoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const foto = canvas.toDataURL("image/jpeg", 0.7).split(",")[1];
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCamaraActiva(false);
    setFotoBase64(foto);
  };

  const registrarConClave = async () => {
    if (!usuarioSel) return handleOpenAlert("Seleccione un usuario", "warning");
    if (!clave.trim()) {
      handleOpenAlert("Capture su clave de acceso", "warning");
      claveRef.current?.focus();
      return;
    }
    if (registrando) return;
    const tipo = modoRegistro === "auto" ? "auto" : tipoManual;
    if (tipo !== "auto" && tiposRegistrados.includes(tipo)) {
      handleOpenAlert(`El registro "${tipo}" ya fue capturado hoy`, "info");
      return;
    }

    setRegistrando(true);
    try {
      const respuesta = await postRequestRef.current({
        url: apiTiemposRegistrar,
        body: {
          usuario: usuarioSel.usuario,
          clave: clave.trim(),
          tipo,
          foto: fotoBase64 || null,
          origen: "manual",
        },
      });
      if (respuesta.hasError) {
        handleOpenAlert(respuesta.message || "Error al registrar", "error");
        return;
      }
      await cancelarLector("registro_manual_confirmado", false);
      setClave("");
      setFotoBase64(null);
      await cargarRegistrosHoy(usuarioSel.usuario, true);
      handleOpenAlert(`${respuesta.result?.tipo || tipo} registrada exitosamente`, "success");
    } finally {
      setRegistrando(false);
      claveRef.current?.focus();
    }
  };

  const abrirEnrolamiento = async () => {
    if (!usuarioSel?.id) {
      handleOpenAlert("Seleccione un usuario para enrolar su huella", "warning");
      return;
    }
    await cancelarLector("iniciar_enrolamiento");
    setModalEnrolamiento(true);
  };

  const completarEnrolamiento = async () => {
    setLectorEstado("confirmado");
    setLectorMensaje("Huella guardada correctamente");
    await cargarHuellaUsuario(usuarioSel?.usuario);
  };

  const cerrarEnrolamiento = async (huellaGuardada) => {
    setModalEnrolamiento(false);
    if (!usuarioSel?.usuario || document.visibilityState !== "visible") return;
    if (huellaGuardada) {
      await cargarHuellaUsuario(usuarioSel.usuario);
      openAlertRef.current("Huella registrada y sincronizada correctamente", "success");
    }
    await armarLector(
      usuarioSel.usuario,
      modoRegistro === "auto" ? "auto" : tipoManual,
      "usuario_seleccionado"
    );
  };

  const lectorColor = lectorEstado === "armado"
    ? "primary"
    : lectorEstado === "confirmado" ? "success" : lectorEstado === "error" ? "error" : "default";

  return (
    <Fragment>
      <SizeSoftXLayout
        titlePage="Control de Asistencias"
        buttonActions={(
          <MnuControlAsistenciaActions
            handleClickRegresarMainDashboard={handleClickRegresarMainDashboard}
            onLimpiar={() => seleccionarUsuario(null)}
          />
        )}
      >
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTimeIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {new Date().toLocaleDateString("es-MX", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
              </Typography>
            </Stack>
            <ToggleButtonGroup exclusive size="small" value={modoRegistro} onChange={cambiarModo}>
              <ToggleButton value="auto">Automético</ToggleButton>
              <ToggleButton value="manual">Manual</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Paper>

        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="center">
            <Autocomplete
              options={usuarios}
              getOptionLabel={(opcion) => `${opcion.usuario} - ${opcion.nombre}`}
              value={usuarioSel}
              onChange={(_, valor) => seleccionarUsuario(valor)}
              sx={{ minWidth: 300, flex: 1 }}
              renderInput={(params) => <TextField autoComplete="off" {...params} label="Usuario" size="small" />}
            />

            <Button
              variant="outlined"
              startIcon={<FingerprintIcon />}
              onClick={abrirEnrolamiento}
              disabled={!usuarioSel}
            >
              Enrolar huella
            </Button>

            {modoRegistro === "manual" && (
              <FormControl size="small" sx={{ minWidth: 230 }}>
                <InputLabel>Tipo de registro</InputLabel>
                <Select value={tipoManual} label="Tipo de registro" onChange={cambiarTipoManual}>
                  {TIPOS_TIEMPO.map((tipo) => (
                    <MenuItem key={tipo} value={tipo} disabled={tiposRegistrados.includes(tipo)}>{tipo}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              inputRef={claveRef}
              label="Clave de acceso"
              type="password"
              value={clave}
              onChange={(evento) => setClave(evento.target.value)}
              onKeyDown={(evento) => {
                if (evento.key === "Enter") {
                  evento.preventDefault();
                  registrarConClave();
                }
              }}
              autoComplete="off"
              disabled={!usuarioSel}
              size="small"
              InputProps={{ startAdornment: <LockIcon fontSize="small" sx={{ mr: 0.5, color: "grey.500" }} /> }}
            />

            <Button variant="contained" onClick={registrarConClave} disabled={!usuarioSel || registrando}>
              Registrar
            </Button>

            {!camaraActiva && !fotoBase64 && (
              <Button variant="outlined" startIcon={<CameraAltIcon />} onClick={iniciarCamara} disabled={!usuarioSel}>Foto</Button>
            )}
            {camaraActiva && (
              <Stack direction="row" spacing={1} alignItems="center">
                <video ref={videoRef} autoPlay playsInline width={120} height={90} style={{ borderRadius: 4, border: "1px solid #ccc" }} />
                <Button variant="contained" size="small" onClick={capturarFoto}>Capturar</Button>
              </Stack>
            )}
            {fotoBase64 && (
              <Button size="small" onClick={() => setFotoBase64(null)}>Quitar foto</Button>
            )}
          </Stack>
          <Box sx={{ mt: 1.5 }}>
            <Chip icon={<FingerprintIcon />} color={lectorColor} variant={lectorEstado === "armado" ? "filled" : "outlined"} label={lectorMensaje || "Lector inactivo"} />
          </Box>
          {usuarioSel && (
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "primary.main" }}>
                <FingerprintIcon />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                  {usuarioSel.usuario} - {usuarioSel.nombre}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {huellaSel
                    ? huellaSel.sincronizada
                      ? "Huella registrada en el sistema"
                      : "Huella local (no sincronizada)"
                    : "El usuario no tiene una huella registrada"}
                </Typography>
              </Box>
              {huellaSel?.imagen ? (
                <img
                  src={`data:image/png;base64,${huellaSel.imagen}`}
                  alt="Huella del usuario"
                  style={{ width: 96, height: 120, objectFit: "cover", border: "1px solid #ccc", borderRadius: 4 }}
                />
              ) : (
                <Box
                  sx={{
                    width: 96,
                    height: 120,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px dashed #ccc",
                    borderRadius: 1,
                  }}
                >
                  <FingerprintIcon sx={{ fontSize: 40, color: "grey.400" }} />
                </Box>
              )}
            </Box>
          )}
        </Paper>

        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Registros del día</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            {TIPOS_TIEMPO.map((tipo) => {
              const registro = registrosHoy.find((item) => item.tiporegistro?.trim() === tipo);
              const registrado = Boolean(registro);
              return (
                <Box key={tipo} sx={{ p: 1.5, minWidth: 190, flex: 1, border: "1px solid", borderColor: registrado ? "success.main" : "divider", bgcolor: registrado ? "#f1f8e9" : "background.paper", borderRadius: 1 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{tipo}</Typography>
                      {registrado && <Typography variant="caption" color="text.secondary">{registro.hora?.trim()}</Typography>}
                    </Box>
                    {registrado ? <CheckCircleIcon color="success" fontSize="small" /> : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Paper>

        {registrosHoy.length > 0 && (
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Historial de hoy</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow><TableCell>Tipo</TableCell><TableCell>Hora</TableCell></TableRow></TableHead>
                <TableBody>{registrosHoy.map((registro) => (
                  <TableRow key={registro.id}><TableCell>{registro.tiporegistro?.trim()}</TableCell><TableCell>{registro.hora?.trim()}</TableCell></TableRow>
                ))}</TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
        <canvas ref={canvasRef} style={{ display: "none" }} />
      </SizeSoftXLayout>
      <ModalEnrolarHuella
        open={modalEnrolamiento}
        usuario={usuarioSel}
        perfil={PERFIL_HUELLA}
        seguridadUrl={serverSeguridad}
        administrativoUrl={serverAdministrativo}
        systemAccessToken={PERFIL_HUELLA === "original"
          ? localStorage.getItem("sizesoftx-dashboard-token") || ""
          : ""}
        tenantAccessToken={PERFIL_HUELLA === "tenant"
          ? localStorage.getItem("sizesoftx-tenant-access-token") || ""
          : ""}
        onCompleted={completarEnrolamiento}
        onClose={cerrarEnrolamiento}
      />
    </Fragment>
  );
}
